import { isInMatch } from '../domain/machine';
import type { InMatchState } from '../domain/machine';
import type { MatchState } from '../types';
import { describeUnknownError } from './errors';
import { log } from './logger';

/**
 * ENV-8 / NFR-5 — the **screen wake lock**, design §2.6.
 *
 * > ENV-8 requires the hold *"throughout in-match states, including halftime"*.
 *
 * ── The lock is bound to `Match.state`, never to a mounted screen ────────────
 *
 * §2.6 is explicit, and the reason is the failure that reads as correct:
 *
 * > Halftime's screen is U-13's, the Set Piece sheet is U-12's, Discovery is U-8's — a lock acquired
 * > on mount and released on unmount would drop on every one of those navigations and, worse, would
 * > be *correct on every screen individually* while failing the requirement.
 *
 * So there is **no release path tied to a lifecycle**. {@link syncWakeLock} is called with a
 * `Match.state` and decides from the §2 grouping; the caller that stops rendering does not call
 * anything. `hold` takes {@link InMatchState} rather than `MatchState` so an out-of-grouping
 * acquisition is a **compile error**, which is what stops a sixth path hand-rolling a sixth
 * acquisition (§1.2's `wakeLock.hold(s: InMatchState)` signature).
 *
 * ── Re-acquisition, and the one place it must NOT happen ─────────────────────
 *
 * WebKit releases the lock whenever the page stops being visible and fires the sentinel's `release`
 * event; it does **not** re-acquire on return. §2.6 therefore puts re-acquisition in the
 * `visibilitychange` handler `bootstrap.ts` already owns — *"one listener with two jobs, not a second
 * lifecycle mechanism"* — and forbids it in the sentinel's own handler:
 *
 * > The `release` event fires *because* the page was hidden; requesting a wake lock from a hidden
 * > page rejects (`NotAllowedError`), and a handler that retried there would produce a retry loop
 * > that runs precisely while the phone is in a pocket — a battery-hostile loop, which NFR-5 forbids
 * > by name.
 *
 * {@link onSentinelRelease} therefore records state and logs at `info`. Nothing else. There is no
 * timer, no interval and no polling anywhere in this module, and `wakeLock.test.ts` asserts that
 * against the source text rather than trusting the comment.
 *
 * ── Failure is surfaced, never swallowed, and never blocks a tap ─────────────
 *
 * A rejected request raises the §5.6 notification-strip chip and logs at `warn`. On a runtime with
 * no `navigator.wakeLock` the app runs unlocked and shows the chip once. There is no polyfill and no
 * `NoSleep`-style video hack — that would be background media playback, banned by NFR-5. Capture is
 * unaffected either way: the lock is a comfort, not a dependency (ENV-9).
 *
 * ── ENV-1 ────────────────────────────────────────────────────────────────────
 *
 * Everything about whether the *screen actually stays awake* is device-only (VER-3 item 4). What
 * this module and its tests can carry is which requests are issued, when, and — more importantly —
 * which are **not**.
 */

const MODULE = 'wakeLock';

/** The one call §2.6 makes, as an interface rather than a global (the `writerLock` argument). */
export interface WakeLockSentinelLike {
  release(): Promise<void>;
  addEventListener(type: 'release', listener: () => void): void;
}

export interface WakeLockManagerLike {
  request(type: 'screen'): Promise<WakeLockSentinelLike>;
}

export interface WakeLockOptions {
  /**
   * The manager to request from. Omitted takes `navigator.wakeLock`; `null` runs **unlocked**, which
   * is what a runtime without the API does and is therefore a real production path, not a stub.
   */
  readonly wakeLock?: WakeLockManagerLike | null;
  /**
   * Where {@link armActivationRetry} listens for the user's first touch. Defaults to `window`.
   *
   * A parameter for the reason `bootstrap.ts`'s two event targets are parameters: the Node suite has
   * no `window`, and *a wiring obligation that no test can exercise is one the next unit
   * rediscovers in the field.* Both halves of §2.6's resume behaviour — that the refusal is
   * re-attempted once on a touch, and that it is **not** re-attempted a second time — are only
   * assertable if a test can deliver that touch.
   */
  readonly activationTarget?: EventTarget;
}

export interface WakeLockStatus {
  /** Whether a sentinel is currently held. */
  readonly held: boolean;
  /** Whether the runtime offers the API at all. `false` is the ENV-8-cannot-be-met case. */
  readonly supported: boolean;
  /** §5.6's chip: the API is missing, or the last request in an in-match state was refused. */
  readonly degraded: boolean;
  /** The `Match.state` this module was last synced to. `null` before any surface has reported one. */
  readonly boundState: MatchState | null;
}

let sentinel: WakeLockSentinelLike | null = null;
let boundState: MatchState | null = null;
let supported = true;
let refused = false;
/**
 * Guards a second request while the first is still in flight.
 *
 * §17.1 asks for idempotence on *"a second entry with a held sentinel"*, which `sentinel !== null`
 * answers. This covers the case one step earlier: two entries into an in-match state a tick apart —
 * a transition immediately followed by a `visibilitychange` — would otherwise issue two requests and
 * leak the first sentinel, which is a lock nothing can ever release.
 */
let requesting = false;
/**
 * Whether this page load has already spent its one post-refusal re-attempt. See
 * {@link armActivationRetry}.
 */
let activationRetrySpent = false;
/** The armed listener, so it can be removed the instant it fires and by the test seam. */
let activationRetry: (() => void) | null = null;
/** What it was armed on, so the test seam can detach it from the same target. */
let activationTargetRef: EventTarget | null = null;

type Listener = (status: WakeLockStatus) => void;
const listeners = new Set<Listener>();

export function wakeLockStatus(): WakeLockStatus {
  return { held: sentinel !== null, supported, degraded: !supported || refused, boundState };
}

/** Subscribes the §5.6 strip to the chip's condition. Returns the unsubscribe. */
export function subscribeWakeLock(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notify(): void {
  const status = wakeLockStatus();
  for (const listener of listeners) listener(status);
}

/** `navigator.wakeLock`, or `null` on a runtime without it (Node under test; an older browser). */
function hostWakeLock(): WakeLockManagerLike | null {
  if (typeof navigator === 'undefined') return null;
  const api = (navigator as { wakeLock?: WakeLockManagerLike }).wakeLock;
  return api ?? null;
}

function resolveManager(options: WakeLockOptions): WakeLockManagerLike | null {
  return options.wakeLock === undefined ? hostWakeLock() : options.wakeLock;
}

/**
 * The sentinel's own `release` event.
 *
 * **It issues no request.** See the module note: it fires because the page was hidden, and retrying
 * from a hidden page is the battery-hostile loop NFR-5 bans by name. The next `visibilitychange` to
 * visible is what re-acquires, and only while `isInMatch(boundState)`.
 */
function onSentinelRelease(): void {
  sentinel = null;
  log.info(
    MODULE,
    'ENV-8: the platform released the screen wake lock (the page stopped being visible). It is re-acquired on the next visibilitychange while in-match, and deliberately not from here.',
    { boundState },
  );
  notify();
}

/**
 * ENV-8 — acquire, idempotently, for an in-match state.
 *
 * The parameter type is the enforcement: `InMatchState` excludes `preMatch`, `fullTime`, `triage`,
 * `distill`, `archived` and `idle`, so §2.6's negative — *"no request in \[those states\]"* — is a
 * compile error at every call site rather than a rule someone has to remember at six of them.
 */
export async function hold(state: InMatchState, options: WakeLockOptions = {}): Promise<void> {
  boundState = state;
  if (sentinel !== null || requesting) return;

  const manager = resolveManager(options);
  if (manager === null) {
    if (supported) {
      supported = false;
      log.warn(
        MODULE,
        'ENV-8: this runtime has no Screen Wake Lock API, so the display may sleep during the match. Capture is unaffected; there is no polyfill (NFR-5 bans the video hack).',
        { boundState },
      );
      notify();
    }
    return;
  }

  requesting = true;
  try {
    const acquired = await manager.request('screen');
    acquired.addEventListener('release', onSentinelRelease);

    // The state may have moved while the request was in flight — a Full Time held a moment after
    // entering the half. Releasing here is what keeps §2.6's "release happens within one state
    // transition" true even when the transition wins the race.
    if (boundState === null || !isInMatch(boundState)) {
      await releaseSentinel(acquired);
      return;
    }

    sentinel = acquired;
    supported = true;
    refused = false;
    log.info(MODULE, 'ENV-8: screen wake lock held', { boundState });
    notify();
  } catch (cause) {
    // NFR-8 — surfaced, never swallowed. `NotAllowedError` is the ordinary case (a hidden page, or
    // the user's low-power mode); it raises the strip chip and blocks nothing.
    refused = true;
    log.warn(MODULE, 'ENV-8: the screen wake lock was refused; the display may sleep', {
      boundState,
      error: describeUnknownError(cause),
    });
    notify();
    armActivationRetry(options);
  } finally {
    requesting = false;
  }
}

/**
 * §2.6's resume case, which had no mechanism until U-20's route table produced one that needed it.
 *
 * ── The platform fact, measured on the engine rather than assumed ───────────
 *
 * **WebKit refuses `wakeLock.request('screen')` until the page has had user activation.** Measured
 * in Playwright WebKit: `NotAllowedError` at load + 0, 100, 300, 700, 1500 and 3000 ms, and `OK` on
 * the first click. Waiting is not the variable; a gesture is. §0.3's rule — *a capability claim
 * lives in that table or in a runtime probe, and nowhere else* — is why this is written down beside
 * the code that depends on it rather than assumed at the call site.
 *
 * ── Why that makes a resume different from every other acquisition ──────────
 *
 * Every other path into `hold` follows a user's own tap: a Kickoff hold, a Half hold, a return to
 * the foreground. **SES-3's resume does not.** The app opens straight onto the capture surface
 * §2.5 names, the chrome reports its `Match.state`, and the request is issued before the user has
 * touched anything — so it is refused, and §2.6's trigger set has nothing between that refusal and
 * the next `visibilitychange` or `pageshow`, neither of which fires again. The screen was then free
 * to sleep for the whole of a resumed half, which is precisely the failure §2.6's positive says
 * must not happen: *"After a process kill and SES-3 resume into an in-match state, the screen does
 * not sleep."* The requirement asserted the property; nothing enforced it, because until this cycle
 * nothing could reach the resume path.
 *
 * ── Why this is not the retry loop NFR-5 and §2.6 both forbid ───────────────
 *
 * §2.6 bans re-requesting from the sentinel's `release` handler because that handler fires *because
 * the page was hidden*, so retrying there loops while the phone is in a pocket. This is the
 * opposite in every respect:
 *
 * - It arms **only after a refusal**, and only while `isInMatch(boundState)`.
 * - It is **one attempt per page load** (`activationRetrySpent`). A second refusal does not re-arm;
 *   the chip stays up and the log says why. There is no cadence, no timer and no interval.
 * - The listener **removes itself before doing any work**, so the tap that triggers it pays a
 *   removal and nothing else — the request runs off the tap path, as everything in this module does
 *   (ENV-9: nothing blocks a tap).
 * - `once: true` is belt to the removal's braces, not a substitute for it.
 */
function armActivationRetry(options: WakeLockOptions): void {
  if (activationRetrySpent || activationRetry !== null) return;
  const target = resolveActivationTarget(options);
  if (target === null || boundState === null || !isInMatch(boundState)) return;
  activationRetrySpent = true;

  const onActivation = (): void => {
    target.removeEventListener('pointerdown', onActivation, true);
    activationRetry = null;
    if (boundState === null || !isInMatch(boundState) || sentinel !== null) return;
    void hold(boundState, options);
  };

  activationRetry = onActivation;
  activationTargetRef = target;
  target.addEventListener('pointerdown', onActivation, { capture: true, once: true, passive: true });
}

/** `window`, the injected target, or `null` on a runtime with neither (the Node suite). */
function resolveActivationTarget(options: WakeLockOptions): EventTarget | null {
  if (options.activationTarget !== undefined) return options.activationTarget;
  return typeof window === 'undefined' ? null : window;
}

/** ENV-8 — release, idempotently. *"Release is as much a requirement as the hold."* */
export async function release(): Promise<void> {
  const held = sentinel;
  sentinel = null;
  if (held === null) return;
  await releaseSentinel(held);
  log.info(MODULE, 'ENV-8: screen wake lock released', { boundState });
  notify();
}

async function releaseSentinel(target: WakeLockSentinelLike): Promise<void> {
  try {
    await target.release();
  } catch (cause) {
    // A sentinel the platform already released rejects here. Reported, not thrown: there is nothing
    // a caller could do, and a throw would propagate out of a state-change effect.
    log.warn(MODULE, 'ENV-8: releasing the screen wake lock failed', {
      error: describeUnknownError(cause),
    });
  }
}

/**
 * **The one call site.** Binds the lock to a `Match.state`.
 *
 * Every path that enters or leaves an in-match state routes through here — each of SES-1's four
 * transitions, SES-6's Abandon, and SES-3's resume after a process kill — because they all end in
 * the surface rendering a `Match.state` and reporting it. Binding to the state rather than to each
 * path is what makes resume carry the lock with no second call site.
 */
export async function syncWakeLock(state: MatchState, options: WakeLockOptions = {}): Promise<void> {
  boundState = state;
  if (isInMatch(state)) return hold(state, options);
  return release();
}

/**
 * §2.6 — re-acquisition on `visibilitychange` → visible (and `pageshow`, via `bootstrap.ts`).
 *
 * Gated on `isInMatch(boundState)`, so a return to the foreground in `preMatch`, `fullTime`,
 * `triage`, `distill`, `archived` or `idle` **requests nothing**. That negative is half of ENV-8.
 */
export async function refreshWakeLock(options: WakeLockOptions = {}): Promise<void> {
  const state = boundState;
  if (state === null || !isInMatch(state)) return;
  return hold(state, options);
}

/** Test seam. Drops any sentinel reference and returns the module to its pre-bootstrap state. */
export function __resetWakeLockForTests(): void {
  if (activationRetry !== null && activationTargetRef !== null) {
    activationTargetRef.removeEventListener('pointerdown', activationRetry, true);
  }
  activationTargetRef = null;
  sentinel = null;
  boundState = null;
  supported = true;
  refused = false;
  requesting = false;
  activationRetrySpent = false;
  activationRetry = null;
  listeners.clear();
}
